import {
  DataSource,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  OneDataCollection,
  RecordType,
  SortingsDefinition,
  SummariesDefinition,
} from '@factorialco/factorial-one-react/dist/experimental'
import {
  File,
  FileFilled,
  Folder,
  Graph,
  Image,
  Proyector,
  Video,
} from '@factorialco/factorial-one-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { formatFileSize } from '../utils'

export type FileItem = {
  filename: string
  title?: string | null
  contentType: string
  size: number
  id: number
  type: 'training' | 'trainingClass' | 'membership'
} & RecordType

type RenderableVisualization = 'card' | 'table'

export const FilesList = ({
  source,
  visualizations = ['card', 'table'],
  emptyStates,
}: {
  source: DataSource<
    FileItem,
    FiltersDefinition,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<FileItem>,
    NavigationFiltersDefinition,
    GroupingDefinition<FileItem>
  >
  visualizations?: RenderableVisualization[]
  emptyStates?: {
    'no-data': {
      title: string
      description: string
    }
  }
}) => {
  return (
    <OneDataCollection
      emptyStates={emptyStates}
      source={source}
      visualizations={[
        {
          type: 'card' as const,
          options: {
            title: (item: FileItem) => item.title || item.filename,
            description: (item: FileItem) => formatFileSize(item.size || 0),
            cardProperties: [
              {
                label: '',
                render: (item: FileItem) => {
                  return {
                    type: 'icon' as const,
                    value: {
                      icon: getIcon(item.contentType),
                      label: getFormat(item.contentType).toUpperCase(),
                    },
                  }
                },
              },
            ],
          },
        },
        {
          type: 'table' as const,
          options: {
            columns: [
              {
                label: i18n.t('common.name'),
                render: (item: FileItem) => item.title || item.filename,
              },
              {
                label: i18n.t('common.file_type'),
                render: (item: FileItem) => {
                  return {
                    type: 'icon' as const,
                    value: {
                      label: getFormat(item.contentType).toUpperCase(),
                      icon: getIcon(item.contentType),
                    },
                  }
                },
              },
              {
                label: i18n.t('common.size'),
                render: (item: FileItem) => {
                  return {
                    type: 'text' as const,
                    value: formatFileSize(item.size || 0),
                  }
                },
              },
            ],
          },
        },
      ].filter((visualization) => visualizations.includes(coerceVisualization(visualization.type)))}
    />
  )
}

function coerceVisualization(visualization: string): RenderableVisualization {
  switch (visualization) {
    case 'card':
      return 'card' as const
    case 'table':
      return 'table' as const
    default:
      throw new Error(`Invalid visualization: ${visualization}`)
  }
}

type Icon = typeof Image
function getIcon(mimetype: string): Icon {
  const typeRegex: Array<[RegExp, Icon]> = [
    [/zip/, Folder],
    [/pdf/, FileFilled],
    [/image/, Image],
    [/mp4/, Video],
    [/mov/, Video],
    [/video/, Video],
    [/word|txt/, FileFilled],
    [/excel|csv|spreadsheetml/, Graph],
    [/presentation|powerpoint/, Proyector],
    [/text/, File],
  ]
  const name = typeRegex.find(([regexp]) => mimetype.match(regexp))
  return name ? name[1] : File
}

function getFormat(mimetype: string): string {
  const typeRegex: Array<[RegExp, string]> = [
    [/zip/, 'zip'],
    [/pdf/, 'pdf'],
    [/jpeg|jpg/, 'jpeg'],
    [/png/, 'png'],
    [/mp4/, 'mp4'],
    [/mov/, 'mov'],
    [/video/, 'video'],
    [/word|txt/, 'txt'],
    [/excel|csv|spreadsheetml/, 'xls'],
    [/presentation|powerpoint/, 'ppt'],
    [/text/, 'text'],
  ]
  const name = typeRegex.find(([regexp]) => mimetype.match(regexp))
  return name ? name[1] : mimetype
}
